import React, { useState, useContext } from 'react';
import cn from 'clsx';
import { css } from 'linaria';

import AppLink from '../../AppLink';

import SectionContent from './SectionContent';
import NavPromos from './NavPromos';

import fastpanel from '../../../utils/fastpanel';
import colors from '../../../utils/colorConstants';
import { PageDefaultsContext } from '../../../hooks/usePageDefaults';
import useEdgeExperiment from '../../../hooks/useEdgeExperiment';

const NavOption = css`
  & .nav-content {
    display: none;
  }

  & > a {
    color: var(--element-text-color);
  }

  &.focused > a,
  &:active > a {
    color: var(--element-text-color);
    border-bottom-color: var(--element-underline-color);
  }

  &.has-flyout.focused .nav-content,
  &.has-flyout:active .nav-content {
    display: block;
  }
`;

const PromotedOption = css`
  background-color: var(--element-bg-color);

  &.focused > a > span,
  &:active > a > span {
    background-color: var(--element-bg-highlight-color);
  }
`;

const SectionNav = () => {
  const { navNodes = [] } = useContext(PageDefaultsContext);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const election = useEdgeExperiment('pro_nav_v2');
  const isVariant = election?.variantId === 2;

  // bubble up click actions to a higher level so we can handle them in one place...
  const clearFocusedNav = (event: any, navLevel: any) => {
    const {
      target,
      target: {
        parentNode,
        parentNode: { tagName: parentTagName },
        tagName: targetTagName,
      },
    } = event;
    let targetHREF;
    if (targetTagName === 'A') {
      targetHREF = target.href;
    } else if (parentTagName === 'A') {
      targetHREF = parentNode.href;
    }

    // If target or its parent is a link, then we close
    if (targetHREF) {
      fastpanel({
        name: 'pbr_nav_finished',
        data: {
          href: targetHREF,
          navLevel,
        },
      });
      clearFocus();
    }
  };
  const clearFocus = () => {
    fastpanel({ name: 'pbr_modal_closed', data: { id: 'nav' } });
    setFocusedIndex(-1);
  };
  return (
    <nav className="relative px-8 border-b border-dove z-10">
      <ul className="flex justify-center list-none">
        {navNodes.map(
          ({ childNodes, color, customUrl, id, promos, slug, title, type }: any, index) => {
            const hasChildNodes = childNodes.length > 0;
            const navLevel = [undefined, title];
            const textColor = color ? color : colors.lapel;
            let underlineColor;

            if (isVariant) {
              underlineColor = color ? color : id !== '1073' ? colors.lapel : 'transparent';
            } else {
              underlineColor = color ? 'transparent' : colors.paloma;
            }

            return (
              <li
                className={cn(NavOption, isVariant && id === '1073' && PromotedOption, {
                  focused: focusedIndex === index,
                  'has-flyout': hasChildNodes,
                })}
                key={slug}
                onMouseEnter={() => {
                  if (hasChildNodes) {
                    fastpanel({
                      name: 'pbr_modal_opened',
                      data: { id: 'nav' },
                    });
                  }
                  setFocusedIndex(index);
                }}
              >
                <AppLink
                  className={cn(
                    `flex items-center font-bold h-12 tracked-medium text-center mx-5 border-b-4 border-white hover:border-transparent box-border`,
                    { 'border-b-2': isVariant, 'border-b-4': !isVariant }
                  )}
                  href={customUrl || `/cards/${type}/${slug}`}
                  onClick={(event: any) => clearFocusedNav(event, navLevel)}
                  onMouseLeave={
                    !hasChildNodes ? (event: any) => clearFocusedNav(event, navLevel) : undefined
                  }
                  style={{
                    // @ts-expect-error
                    '--element-text-color': textColor,
                    '--element-underline-color': underlineColor,
                  }}
                  tracking={{
                    id: title,
                    block_name: 'category_nav',
                    block_row: 'category_nav',
                  }}
                >
                  {(isVariant === false || id !== '1073') && title}
                  {isVariant && id === '1073' && (
                    <span
                      className={'px-3 py-0.5 rounded-full bg-[#E2EFFF] hover:bg-[#B5D6FF]'}
                      style={{
                        // @ts-expect-error
                        '--element-bg-color': '#E2EFFF',
                        '--element-bg-highlight-color': '#B5D6FF',
                      }}
                    >
                      {title}
                    </span>
                  )}
                </AppLink>
                {hasChildNodes && (
                  <div
                    className={`nav-content paragraph-small shadow-md absolute top-12 left-0 right-0 max-w-full bg-white`}
                    onMouseLeave={() => setFocusedIndex(-1)}
                  >
                    <div className="flex justify-center p-8">
                      <SectionContent
                        childNodes={childNodes}
                        navLevel={navLevel}
                        onNavFinished={clearFocusedNav}
                      />
                      <NavPromos
                        navLevel={[...navLevel, `Promo Block ${index + 1}`]}
                        onNavFinished={clearFocusedNav}
                        promos={promos}
                      />
                    </div>
                  </div>
                )}
              </li>
            );
          }
        )}
      </ul>
    </nav>
  );
};

export default SectionNav;
